import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash-es';
import withLoader from '../../components/Loader/withLoader.js';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

// Components
import AccountsLayout from '../../components/AccountsLayout/AccountsLayout';
import AccountsSection from '../../components/AccountsSection/AccountsSection';
import BalanceCard from '../../components/AccountsSection/BalanceCard/BalanceCard';
import HelpCard from '../../components/HelpCard/HelpCard.js';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';

// Icons
import AccountIcon from '@material-ui/icons/AccountBalanceOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import MaintenanceIcon from '@material-ui/icons/BuildOutlined';

import { getString, stringToDate } from '../../helpers/stringHelpers.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { isFeatureDisabled } from '../../helpers/outageHelpers';
import analyticsApi from '../../api/analytics.js';
import constants from '../../constants';

class Accounts extends React.Component {
  componentDidMount() {
    const { setAppBarTitle } = this.props;
    const stringsComponent = 'accounts';

    setAppBarTitle(getString(stringsComponent, 'TITLE'));
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status.
    if (getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED') {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, account } = props;
    const loaded = [tenancy.loaded, account.loaded];
    return loaded;
  }

  render() {
    const {
      signout,
      getAccounts,
      tenancy,
      account,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      goAccountDetails,
      goArrangementDetails,
      goArrangementCreate,
      toggleContactModal,
      onLoadComplete,
      goDash,
      roles,
    } = this.props;

    const tenancyData = tenancy.data;
    const accountData = account.data;
    const stringsComponent = 'accounts';

    const rentBalanceAmount =
      accountData.hasOwnProperty('accounts') &&
      accountData.accounts.hasOwnProperty('rent')
        ? Number(accountData.accounts.rent.accountBalance)
        : 0;

    const arrangementFeatureDisabled = isFeatureDisabled(
      'ARRANGEMENTS_WRITE',
      tenancyData.area
    );

    const rentBalance = (
      <BalanceCard
        title={getString(stringsComponent, 'RENT_TITLE')}
        balance={rentBalanceAmount}
        label={
          getString(stringsComponent, 'DATE_LABEL') +
          constants.TEXT_SEPERATOR +
          stringToDate(new Date())
        }
        paymentReference={
          accountData.hasOwnProperty('accounts') &&
          accountData.accounts.hasOwnProperty('rent')
            ? getString(stringsComponent, 'PAYMENT_REFERENCE') +
              constants.TEXT_SEPERATOR +
              accountData.accounts.rent.paymentReference
            : null
        }
        icon={<AccountIcon />}
        helpText={portalContent.DASH_RENT}
        gaEvent={() => analyticsApi.event('Help', 'Click', 'Rent Balance')}
        action={() => goAccountDetails('rent')}
        actionLabel={getString(stringsComponent, 'VIEW_DETAILS_LABEL')}
        actionTwo={
          get(accountData, 'accounts.rent.arrangements[0]', null)
            ? () => goArrangementDetails('rent')
            : null
        }
        actionTwoLabel={getString(stringsComponent, 'VIEW_ARRANGEMENTS_LABEL')}
        showArrangementAction={
          rentBalanceAmount != 0 &&
          !get(accountData, 'accounts.rent.arrangements', false)
        }
        hasMaintenanceArrangement={Boolean(
          get(accountData, 'accounts.maintenance.arrangements', false)
        )}
        arrangementFeatureDisabled={arrangementFeatureDisabled}
        arrangementCreateAction={() => goArrangementCreate('rent')}
        toggleContactModal={toggleContactModal}
        customerRoles={roles}
      />
    );

    const repairsBalanceAmount =
      accountData.hasOwnProperty('accounts') &&
      accountData.accounts.hasOwnProperty('maintenance')
        ? Number(accountData.accounts.maintenance.accountBalance)
        : 0;

    const repairsBalance = (
      <BalanceCard
        title={getString(stringsComponent, 'REPAIRS_TITLE')}
        balance={repairsBalanceAmount}
        label={
          getString(stringsComponent, 'DATE_LABEL') +
          constants.TEXT_SEPERATOR +
          stringToDate(new Date())
        }
        paymentReference={
          accountData.hasOwnProperty('accounts') &&
          accountData.accounts.hasOwnProperty('maintenance')
            ? getString(stringsComponent, 'PAYMENT_REFERENCE') +
              constants.TEXT_SEPERATOR +
              accountData.accounts.maintenance.paymentReference
            : null
        }
        icon={<MaintenanceIcon />}
        helpText={portalContent.DASH_REPAIRS}
        gaEvent={() =>
          analyticsApi.event('Help', 'Click', 'Maintenance Balance')
        }
        action={() => goAccountDetails('maintenance')}
        actionLabel={getString(stringsComponent, 'VIEW_DETAILS_LABEL')}
        actionTwo={
          get(accountData, 'accounts.maintenance.arrangements[0]', null)
            ? () => goArrangementDetails('maintenance')
            : null
        }
        actionTwoLabel={getString(stringsComponent, 'VIEW_ARRANGEMENTS_LABEL')}
        showArrangementAction={
          repairsBalanceAmount != 0 &&
          !get(accountData, 'accounts.maintenance.arrangements', false)
        }
        isMaintenanceBalance={true}
        arrangementFeatureDisabled={arrangementFeatureDisabled}
        hasRentArrangementOrDebt={
          Boolean(get(accountData, 'accounts.rent.arrangements[0]', false)) ||
          rentBalanceAmount < 0
        }
        arrangementCreateAction={() => goArrangementCreate('maintenance')}
        toggleContactModal={toggleContactModal}
        customerRoles={roles}
      />
    );

    const help = portalGuidances.find(
      (guidance) => guidance.Title === stringsComponent
    ) && (
      <HelpCard
        title={getString('help', 'TITLE')}
        subheading={getString('help', 'SUBHEADING')}
        content={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).Content
        }
        contact={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).Contact
        }
        faqs={
          portalGuidances.find(
            (guidance) => guidance.Title === stringsComponent
          ).FAQs
        }
        icon={<InfoIcon />}
        toggleContactModal={toggleContactModal}
        gaEvent={analyticsApi.event}
      />
    );

    const accountsSection = (
      <AccountsSection
        rentBalance={rentBalance}
        maintenanceBalance={repairsBalance}
        help={help}
      />
    );

    const WrappedAccountsLayout = withLoader(AccountsLayout);

    return (
      <WrappedAccountsLayout
        currentBalances={accountsSection}
        loaded={this.getLoadedStatuses()}
        onRefresh={getAccounts}
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={portalContent.APP_NOT_FOUND_MESSAGE}
        onLoadComplete={onLoadComplete}
        backAction={goDash}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  account: state.customer.account,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  outageMessage: state.app.outageMessage,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Accounts);
